// src/screens/admin/orderDetail/OrderDetail.jsx

import { useState, useRef, useEffect, useContext } from "react";
import {
  ArrowLeft,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  Banknote,
  ChevronDown,
  Check,
  Ban,
  AlertTriangle,
  Palette,
  Ruler,
  Layers,
  ImageIcon,
  MessageSquare,
  Tag,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/css/admin/orders/AdminOrderView.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// TIMELINE HELPERS
// ─────────────────────────────────────────────────────────────

// Keys & values: all lowercase (matches API exactly)
const NORMAL_FLOW = ["order placed", "confirmed", "in progress", "completed"];
const CANCELED_STEP = "canceled";

const nowString = () => {
  const d = new Date();
  return (
    d.toLocaleDateString("en-PK", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }) +
    " · " +
    d.toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" })
  );
};

// Build initial timeline from created_at + current status
// (API does not return a timeline array)
const buildInitialTimeline = (order) => {
  const status = order.status; // already lowercase from API
  const placedDate = order.created_at
    ? new Date(order.created_at).toLocaleDateString("en-PK", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      }) +
      " · " +
      new Date(order.created_at).toLocaleTimeString("en-PK", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  if (status === CANCELED_STEP) {
    return [
      { status: "order placed", date: placedDate, done: true },
      { status: "canceled", date: nowString(), done: true },
    ];
  }

  const currentIndex = NORMAL_FLOW.indexOf(status);
  return NORMAL_FLOW.map((step, i) => ({
    status: step,
    done: i <= currentIndex,
    date: i === 0 ? placedDate : "—",
  }));
};

const buildTimeline = (prevTimeline, newStatus) => {
  if (newStatus === CANCELED_STEP) {
    return [
      ...prevTimeline.filter((t) => t.done && t.status !== CANCELED_STEP),
      { status: "canceled", date: nowString(), done: true },
    ];
  }
  const newIndex = NORMAL_FLOW.indexOf(newStatus);
  return NORMAL_FLOW.map((step, i) => {
    const existing = prevTimeline.find((t) => t.status === step);
    return {
      status: step,
      done: i <= newIndex,
      date:
        i < newIndex && existing?.date && existing.date !== "—"
          ? existing.date
          : i === newIndex
            ? nowString()
            : "—",
    };
  });
};

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// key   → lowercase  (matches API value & internal state)
// label → Sentence case  (shown in UI)
// ─────────────────────────────────────────────────────────────

// Options shown in the status dropdown — values are lowercase keys
const STATUS_OPTIONS = ["pending", "in progress", "completed", "canceled"];

// key: lowercase  →  label + icon + className
const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    icon: Clock,
    className: "ad-od-status--pending",
  },
  "in progress": {
    label: "In Progress",
    icon: Package,
    className: "ad-od-status--processing",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle,
    className: "ad-od-status--delivered",
  },
  canceled: {
    label: "Canceled",
    icon: XCircle,
    className: "ad-od-status--cancelled",
  },
};

// key: lowercase
const PAYMENT_STATUS_CONFIG = {
  paid: { label: "Paid", className: "ad-od-pay--paid" },
  pending: { label: "Pending", className: "ad-od-pay--pending" },
  refunded: { label: "Refunded", className: "ad-od-pay--refunded" },
};

const ORDER_TYPE_LABEL = {
  normal: { label: "Normal Order", className: "ad-od-type--normal" },
  customized: { label: "Customized Product", className: "ad-od-type--custom" },
  custom_request: { label: "Custom Request", className: "ad-od-type--request" },
};

// Timeline display labels (lowercase key → Sentence case label)
const TIMELINE_LABEL = {
  "order placed": "Order Placed",
  confirmed: "Confirmed",
  "in progress": "In Progress",
  completed: "Completed",
  canceled: "Canceled",
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGE
// receives lowercase status key, displays sentence-case label
// ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status];
  const Icon = config?.icon;
  return (
    <span className={`ad-od-status-badge ${config?.className || ""}`}>
      {Icon && <Icon size={12} />}
      {config?.label || status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS DROPDOWN
// value & onChange both use lowercase keys
// ─────────────────────────────────────────────────────────────
const StatusDropdown = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="ad-od-status-dropdown-wrapper" ref={wrapperRef}>
      <button
        className={`ad-od-status-dropdown-btn ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setIsOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={disabled ? "Status cannot be changed" : "Change status"}
      >
        <StatusBadge status={value} />
        {!disabled && (
          <ChevronDown
            size={14}
            className={`ad-od-chevron ${isOpen ? "open" : ""}`}
          />
        )}
      </button>
      {isOpen && (
        <div className="ad-od-status-menu" role="listbox">
          {STATUS_OPTIONS.map((s) => (
            <div
              key={s}
              className={`ad-od-status-option ${value === s ? "active" : ""}`}
              role="option"
              aria-selected={value === s}
              onClick={() => {
                onChange(s);
                setIsOpen(false);
              }}
            >
              <StatusBadge status={s} />
              {value === s && (
                <Check size={13} className="ad-od-status-check" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TIMELINE
// step.status is lowercase key; TIMELINE_LABEL converts to display
// ─────────────────────────────────────────────────────────────
const Timeline = ({ steps }) => (
  <div className="ad-od-timeline">
    {steps.map((step, i) => (
      <div
        key={i}
        className={`ad-od-timeline-step ${step.done ? "done" : "pending"}`}
      >
        <div className="ad-od-timeline-left">
          <div className="ad-od-timeline-dot" />
          {i < steps.length - 1 && <div className="ad-od-timeline-line" />}
        </div>
        <div className="ad-od-timeline-content">
          <p className="ad-od-timeline-label">
            {TIMELINE_LABEL[step.status] || step.status}
          </p>
          <p className="ad-od-timeline-date">{step.date}</p>
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// CUSTOMIZATION DETAILS CARD
// ─────────────────────────────────────────────────────────────
const CustomizationCard = ({ data }) => (
  <div className="ad-od-card ad-od-custom-card">
    <h2 className="ad-od-card-title">
      <Palette size={16} className="ad-od-card-icon" /> Customization Details
    </h2>
    <div className="ad-od-custom-grid">
      {data.woodType && (
        <div className="ad-od-custom-item">
          <Layers size={13} className="ad-od-custom-icon" />
          <div>
            <p className="ad-od-custom-label">Wood Type</p>
            <span className="ad-od-custom-badge ad-od-custom-badge--wood">
              {data.woodType}
            </span>
          </div>
        </div>
      )}
      {data.colorFinish && (
        <div className="ad-od-custom-item">
          <Palette size={13} className="ad-od-custom-icon" />
          <div>
            <p className="ad-od-custom-label">Color / Finish</p>
            <span className="ad-od-custom-badge ad-od-custom-badge--color">
              {data.colorFinish}
            </span>
          </div>
        </div>
      )}
      {data.fabric && (
        <div className="ad-od-custom-item">
          <Tag size={13} className="ad-od-custom-icon" />
          <div>
            <p className="ad-od-custom-label">Fabric</p>
            <span className="ad-od-custom-badge ad-od-custom-badge--fabric">
              {data.fabric}
            </span>
          </div>
        </div>
      )}
      {data.dimensions && (
        <div className="ad-od-custom-item">
          <Ruler size={13} className="ad-od-custom-icon" />
          <div>
            <p className="ad-od-custom-label">Dimensions</p>
            <p className="ad-od-custom-value">{data.dimensions}</p>
          </div>
        </div>
      )}
    </div>
    {data.extraNotes && (
      <div className="ad-od-custom-notes">
        <MessageSquare size={13} className="ad-od-custom-icon" />
        <p>{data.extraNotes}</p>
      </div>
    )}
    {data.addonPrice > 0 && (
      <div className="ad-od-custom-addon">
        <span>Customization Addon</span>
        <span className="ad-od-custom-addon-price">
          + PKR {data.addonPrice.toLocaleString()}
        </span>
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────
// CUSTOM REQUEST DETAILS CARD
// ─────────────────────────────────────────────────────────────
const CustomRequestCard = ({ data }) => (
  <div className="ad-od-card ad-od-custom-card">
    <h2 className="ad-od-card-title">
      <ImageIcon size={16} className="ad-od-card-icon" /> Custom Request Details
    </h2>
    <div className="ad-od-cr-meta-row">
      <div className="ad-od-cr-meta-item">
        <p className="ad-od-custom-label">Budget Range</p>
        <p className="ad-od-custom-value">{data.budgetRange}</p>
      </div>
      {data.quotedPrice && (
        <div className="ad-od-cr-meta-item">
          <p className="ad-od-custom-label">Quoted Price</p>
          <p className="ad-od-cr-quoted">
            PKR {data.quotedPrice.toLocaleString()}
          </p>
        </div>
      )}
      {data.estimatedDays && (
        <div className="ad-od-cr-meta-item">
          <p className="ad-od-custom-label">Est. Delivery</p>
          <p className="ad-od-custom-value">{data.estimatedDays} days</p>
        </div>
      )}
    </div>
    <div className="ad-od-cr-description">
      <p className="ad-od-custom-label" style={{ marginBottom: "0.4rem" }}>
        Customer Description
      </p>
      <p className="ad-od-cr-desc-text">"{data.description}"</p>
    </div>
    {data.referenceImages?.length > 0 && (
      <div className="ad-od-cr-images">
        <p className="ad-od-custom-label" style={{ marginBottom: "0.6rem" }}>
          Reference Images ({data.referenceImages.length})
        </p>
        <div className="ad-od-cr-images-grid">
          {data.referenceImages.map((src, i) => (
            <a
              key={i}
              href={src}
              target="_blank"
              rel="noreferrer"
              className="ad-od-cr-image-wrap"
            >
              <img
                src={src}
                alt={`Reference ${i + 1}`}
                className="ad-od-cr-image"
              />
              <div className="ad-od-cr-image-overlay">View</div>
            </a>
          ))}
        </div>
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────
// CANCEL MODAL
// ─────────────────────────────────────────────────────────────
const CancelModal = ({ orderId, onConfirm, onClose, isCanceling }) => (
  <div
    className="ad-od-modal-overlay"
    onClick={!isCanceling ? onClose : undefined}
  >
    <div className="ad-od-modal" onClick={(e) => e.stopPropagation()}>
      <div className="ad-od-modal-icon-wrap">
        <AlertTriangle size={28} className="ad-od-modal-icon" />
      </div>
      <h3 className="ad-od-modal-title">Cancel Order?</h3>
      <p className="ad-od-modal-desc">
        Are you sure you want to cancel <strong>#{orderId}</strong>? This action
        cannot be undone and the customer will be notified.
      </p>
      <div className="ad-od-modal-actions">
        <button
          className="ad-od-modal-btn ad-od-modal-btn--secondary"
          onClick={onClose}
          disabled={isCanceling}
        >
          Keep Order
        </button>
        <button
          className="ad-od-modal-btn ad-od-modal-btn--danger"
          onClick={onConfirm}
          disabled={isCanceling}
        >
          {isCanceling ? (
            <span className="ad-od-modal-spinner" />
          ) : (
            <>
              <Ban size={14} /> Yes, Cancel
            </>
          )}
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);

  const [order, setOrder] = useState(null); // null = loading
  const [timeline, setTimeline] = useState([]); // built client-side
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    viewOrderDetails(id);
  }, [id]);

  const viewOrderDetails = (id) => {
    axios
      .get(`${baseUrl}/admin/order/adminViewOrderDetails`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { id },
      })
      .then((response) => {
        console.log(response.data);
        setOrder(response.data.order[0]);
        setTimeline(buildInitialTimeline(response.data.order[0]));
      })
      .catch((error) => {
        console.error("Order fetch error:", error);
      });
  };

  // ── Update status API call ──
  const updateStatus = (newStatus) => {
    axios
      .post(
        `${baseUrl}/admin/order/adminUpdateStatus`,
        { id, status: newStatus },
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((response) => {
        console.log(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Guard — render nothing until order loads
  if (!order) {
    return <div className="ad-od-container">Loading...</div>;
  }

  // lowercase comparisons
  const isFinal = order.status === "canceled" || order.status === "completed";
  const canCancel = !isFinal;
  const payConfig = PAYMENT_STATUS_CONFIG[order.paymentStatus] || {};
  const typeConfig = ORDER_TYPE_LABEL[order.type] || ORDER_TYPE_LABEL.normal;

  // Total quantity across all cart items
  const totalQty =
    order.adcart?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

  // ── Status change ──
  const handleStatusChange = (newStatus) => {
    // newStatus is lowercase key from STATUS_OPTIONS
    if (newStatus === order.status) return;
    if (newStatus === "canceled") {
      setShowCancelModal(true);
      return;
    }
    setStatusUpdating(true);
    setTimeout(() => {
      setOrder((prev) => ({ ...prev, status: newStatus }));
      setTimeline((prev) => buildTimeline(prev, newStatus));
      updateStatus(newStatus);
      setStatusUpdating(false);
    }, 600);
  };

  // ── Cancel confirm ──
  const handleCancelConfirm = () => {
    setIsCanceling(true);
    setTimeout(() => {
      setOrder((prev) => ({
        ...prev,
        status: "canceled",
        paymentStatus:
          prev.paymentMethod === "Bank Transfer"
            ? "refunded"
            : prev.paymentStatus,
      }));
      setTimeline((prev) => buildTimeline(prev, "canceled"));
      updateStatus("canceled");
      setIsCanceling(false);
      setShowCancelModal(false);
    }, 1200);
  };

  return (
    <div className="ad-od-container">
      {/* ── Header ── */}
      <div className="ad-od-header">
        <div className="ad-od-header-left">
          <button
            className="ad-od-back-btn"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="ad-od-header-top-row">
              <h1 className="ad-od-title">Order #{order.id}</h1>
              <StatusBadge status={order.status} />
              <span className={`ad-od-type-badge ${typeConfig.className}`}>
                {typeConfig.label}
              </span>
            </div>
            <p className="ad-od-subtitle">
              Placed on{" "}
              {new Date(order.created_at).toLocaleDateString("en-PK", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
              &nbsp;·&nbsp;Last updated{" "}
              {new Date(order.updated_at).toLocaleDateString("en-PK", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
        </div>

        <div className="ad-od-header-right">
          <div className="ad-od-header-actions">
            {canCancel && (
              <button
                className="ad-od-cancel-btn"
                onClick={() => setShowCancelModal(true)}
              >
                <Ban size={14} /> Cancel Order
              </button>
            )}
            <div>
              <p className="ad-od-change-label">
                {isFinal ? "Order Status" : "Update Status"}
              </p>
              <StatusDropdown
                value={order.status}
                onChange={handleStatusChange}
                disabled={isFinal}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Updating toast ── */}
      {statusUpdating && (
        <div className="ad-od-updating-toast">
          <span className="ad-od-toast-spinner" /> Updating status...
        </div>
      )}

      {/* ── Main grid ── */}
      <div className="ad-od-grid">
        {/* ════ LEFT COLUMN ════ */}
        <div className="ad-od-col-left">
          {/* ── Order Items ── */}
          <div className="ad-od-card">
            <h2 className="ad-od-card-title">
              <Package size={16} className="ad-od-card-icon" /> Order Items
            </h2>

            <div className="ad-od-items-list">
              {order.adcart?.map((cartItem) => {
                const product = cartItem.adproduct;
                const imageUrl = product?.productimages?.[0]?.image_path
                  ? `${baseUrlForFile}${product.productimages[0].image_path}`
                  : null;

                return (
                  <div key={cartItem.id} className="ad-od-item-row">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={product?.name}
                        className="ad-od-item-img"
                      />
                    ) : (
                      <div className="ad-od-item-img ad-od-item-img--placeholder">
                        <ImageIcon size={20} />
                      </div>
                    )}

                    <div className="ad-od-item-info">
                      <p className="ad-od-item-name">{product?.name || "—"}</p>
                      <p className="ad-od-item-meta">
                        {product?.adcategory?.name || "—"} · Size:{" "}
                        {product?.size || "—"}
                      </p>
                    </div>

                    <div className="ad-od-item-pricing">
                      <p className="ad-od-item-price">
                        PKR {Number(cartItem.price).toLocaleString()}
                      </p>
                      <p className="ad-od-item-qty">× {cartItem.quantity}</p>
                    </div>

                    <p className="ad-od-item-subtotal">
                      PKR {Number(cartItem.total_price).toLocaleString()}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Price summary */}
            <div className="ad-od-price-summary">
              <div className="ad-od-price-row">
                <span>Shipping Fee</span>
                <span>
                  {order.shippingFee
                    ? `PKR ${Number(order.shippingFee).toLocaleString()}`
                    : "Free"}
                </span>
              </div>
              <div className="ad-od-price-row ad-od-price-total">
                <span>Total</span>
                <span>PKR {Number(order.total_price).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* ── Customization card (type: customized) ── */}
          {order.type === "customized" && order.customization && (
            <CustomizationCard data={order.customization} />
          )}

          {/* ── Custom request card (type: custom_request) ── */}
          {order.type === "custom_request" && order.customRequest && (
            <CustomRequestCard data={order.customRequest} />
          )}

          {/* ── Timeline ── */}
          <div className="ad-od-card">
            <h2 className="ad-od-card-title">
              <Clock size={16} className="ad-od-card-icon" /> Order Timeline
            </h2>
            <Timeline steps={timeline} />
          </div>

          {/* ── Notes ── */}
          {order.notes && (
            <div className="ad-od-card ad-od-notes-card">
              <h2 className="ad-od-card-title">📝 Customer Notes</h2>
              <p className="ad-od-notes-text">{order.notes}</p>
            </div>
          )}
        </div>

        {/* ════ RIGHT COLUMN ════ */}
        <div className="ad-od-col-right">
          {/* ── Customer ── */}
          <div className="ad-od-card">
            <h2 className="ad-od-card-title">Customer</h2>
            <div className="ad-od-customer-block">
              <div className="ad-od-avatar">
                {order.adcustomer?.fullName
                  ?.split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </div>
              <p className="ad-od-customer-name">
                {order.adcustomer?.fullName || "—"}
              </p>
            </div>
            <div className="ad-od-info-list">
              <div className="ad-od-info-row">
                <Mail size={14} className="ad-od-info-icon" />
                <span>{order.adcustomer?.email || "—"}</span>
              </div>
              <div className="ad-od-info-row">
                <Phone size={14} className="ad-od-info-icon" />
                <span>{order.phone || order.adcustomer?.phone || "—"}</span>
              </div>
            </div>
          </div>

          {/* ── Shipping Address ── */}
          <div className="ad-od-card">
            <h2 className="ad-od-card-title">
              <MapPin size={16} className="ad-od-card-icon" /> Shipping Address
            </h2>
            <div className="ad-od-address-block">
              <p>{order.address || "No address provided"}</p>
            </div>
          </div>

          {/* ── Payment ── */}
          <div className="ad-od-card">
            <h2 className="ad-od-card-title">
              <CreditCard size={16} className="ad-od-card-icon" /> Payment
            </h2>
            <div className="ad-od-payment-block">
              <div className="ad-od-payment-row">
                <div className="ad-od-payment-method-icon">
                  {order.paymentMethod === "Bank Transfer" ? (
                    <CreditCard size={15} />
                  ) : (
                    <Banknote size={15} />
                  )}
                </div>
                <div>
                  <p className="ad-od-payment-method-name">
                    {order.paymentMethod}
                  </p>
                  <p className="ad-od-payment-method-sub">
                    {order.paymentMethod === "Bank Transfer"
                      ? "Online payment"
                      : "Pay on delivery"}
                  </p>
                </div>
              </div>
              <div className="ad-od-payment-status-row">
                <span className="ad-od-pay-label">Payment Status</span>
                {/* label from config so display stays Sentence case */}
                <span
                  className={`ad-od-pay-badge ${payConfig.className || ""}`}
                >
                  {payConfig.label || order.paymentStatus}
                </span>
              </div>
            </div>
          </div>

          {/* ── Summary ── */}
          <div className="ad-od-card ad-od-summary-card">
            <h2 className="ad-od-card-title">Summary</h2>
            <div className="ad-od-summary-list">
              <div className="ad-od-summary-row">
                <span className="ad-od-summary-label">Order ID</span>
                <span className="ad-od-summary-value ad-od-summary-id">
                  #{order.id}
                </span>
              </div>
              <div className="ad-od-summary-row">
                <span className="ad-od-summary-label">Order Type</span>
                <span className={`ad-od-type-badge ${typeConfig.className}`}>
                  {typeConfig.label}
                </span>
              </div>
              <div className="ad-od-summary-row">
                <span className="ad-od-summary-label">Items</span>
                <span className="ad-od-summary-value">{totalQty} pcs</span>
              </div>
              <div className="ad-od-summary-row">
                <span className="ad-od-summary-label">Order Total</span>
                <span className="ad-od-summary-value ad-od-summary-total">
                  PKR {Number(order.total_price).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cancel Modal ── */}
      {showCancelModal && (
        <CancelModal
          orderId={order.id}
          onConfirm={handleCancelConfirm}
          onClose={() => !isCanceling && setShowCancelModal(false)}
          isCanceling={isCanceling}
        />
      )}
    </div>
  );
};

export default OrderDetail;
